import React from 'react';
import { Link } from 'react-router-dom';

// Only step 2 gets a route. Step 1 deliberately does not: it leads to the
// recorder, which would let a user overwrite the interview without the
// "Nieuwe opname" confirmation. Steps 3 and 4 are reached by completing the flow.
const steps: Array<{ number: number; label: string; path?: (caseId: string) => string }> = [
  { number: 1, label: 'Gesprek' },
  { number: 2, label: 'Opmerkingen', path: (caseId) => `/case/${caseId}` },
  { number: 3, label: 'Resultaten' },
  { number: 4, label: 'Verzenden' },
];

interface StepperProps {
  step: number;
  // Enables navigation back to an earlier step of this conversation. Without it
  // the stepper stays purely informational.
  caseId?: string;
  // Once the questionnaire has been sent the case is finished, so the stepper
  // stops offering a way back into it.
  isLocked?: boolean;
}

const Stepper: React.FC<StepperProps> = ({ step, caseId, isLocked = false }) => {
  return (
    <nav className="mb-10" aria-label="Voortgang">
      <div className="stepper-track">
        <ol className="stepper-list">
          {steps.map((stepItem) => {
            const isActive = step >= stepItem.number;
            // Only steps already behind the user are navigable, so the stepper
            // can never skip ahead past unfinished work — and never once the
            // questionnaire has been sent.
            const target =
              caseId && step > stepItem.number && !isLocked
                ? stepItem.path?.(caseId)
                : undefined;

            const content = (
              <>
                <span className={`stepper-circle${isActive ? ' stepper-circle-active' : ''}`}>
                  {stepItem.number}
                </span>
                <span className={`stepper-label${isActive ? ' stepper-label-active' : ''}`}>
                  {stepItem.label}
                </span>
              </>
            );

            return (
              <li
                key={stepItem.number}
                className={isActive ? 'stepper-step-active' : 'stepper-step-inactive'}
              >
                {target ? (
                  <Link
                    to={target}
                    aria-label={`Terug naar stap ${stepItem.number}: ${stepItem.label}`}
                    className="flex flex-col items-center gap-2 rounded-lg transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blauw"
                  >
                    {content}
                  </Link>
                ) : (
                  content
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

export default Stepper;
